import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertCircle, FileText, Download, X, ZoomIn, ZoomOut } from 'lucide-react';
import DxfViewer from './DxfViewer';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FilePreview = ({ 
  isOpen, 
  onClose, 
  fileUrl, 
  fileName, 
  fileType,
  originalFileUrl = null
}) => {
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Determine what to show based on the URL (not just filename)
  const getDisplayType = () => {
    if (!fileUrl) return 'unknown';
    
    const urlLower = fileUrl.toLowerCase();
    
    // Check URL extension first (for converted PDFs)
    if (urlLower.endsWith('.pdf') || urlLower.includes('.pdf#') || urlLower.includes('.pdf?')) {
      return 'pdf';
    }
    
    // Check for images in URL
    if (urlLower.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)($|\?|#)/)) {
      return 'image';
    }
    
    // Check for CAD files (DXF)
    if (urlLower.match(/\.dxf($|\?|#)/)) {
      return 'dxf';
    }
    
    // Fall back to fileType prop if provided
    if (fileType === 'image') return 'image';
    if (fileType === 'pdf') return 'pdf';
    if (fileType === 'dxf' || fileType === 'cad') return 'dxf';
    
    // Fall back to filename extension
    if (fileName) {
      const ext = fileName.split('.').pop()?.toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
      if (ext === 'pdf') return 'pdf';
      if (ext === 'dxf') return 'dxf';
      if (['dwg'].includes(ext)) return 'dwg'; // DWG needs conversion
      if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) return 'document';
    }
    
    return 'document';
  };

  const displayType = getDisplayType();
  const downloadUrl = originalFileUrl || fileUrl;
  
  // Get display name for header - ALWAYS show original filename
  const getDisplayName = () => {
    // Always return the original fileName passed to the component
    return fileName || 'Datei';
  };

  // Request fullscreen mode on mobile for immersive experience
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setImageLoaded(false);
      
      // Try to enter fullscreen on mobile
      const enterFullscreen = async () => {
        try {
          const elem = document.documentElement;
          if (elem.requestFullscreen) {
            await elem.requestFullscreen();
          } else if (elem.webkitRequestFullscreen) {
            await elem.webkitRequestFullscreen();
          } else if (elem.mozRequestFullScreen) {
            await elem.mozRequestFullScreen();
          } else if (elem.msRequestFullscreen) {
            await elem.msRequestFullscreen();
          }
        } catch (err) {
          // Fullscreen not available or denied - that's okay
          console.log('Fullscreen not available:', err.message);
        }
      };
      
      // Only try fullscreen on mobile
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isMobile) {
        enterFullscreen();
      }
      
      // Debug logging
      console.log('🔍 FilePreview OPENED:', { fileUrl, fileName, displayType });
    }
    
    return () => {
      // Exit fullscreen when closing
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [isOpen, fileUrl, fileName, fileType, originalFileUrl]);

  if (!isOpen) return null;

  const handleClose = () => {
    // Exit fullscreen before closing
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    onClose();
  };

  // Compact Header component for mobile
  const Header = ({ bgColor = 'bg-gray-800' }) => (
    <div className={`px-3 py-2 flex items-center justify-between ${bgColor} shrink-0 safe-area-top`}>
      <button
        onClick={handleClose}
        className="flex items-center gap-1 text-white hover:text-gray-300 transition-colors p-1"
        aria-label="Zurück"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="text-white text-xs text-center flex-1 mx-2 truncate font-medium">
        {getDisplayName()}
      </div>
      <a 
        href={downloadUrl} 
        download={fileName}
        className="text-white hover:text-gray-300 p-1 transition-colors"
        title="Herunterladen"
        aria-label="Herunterladen"
      >
        <Download className="w-5 h-5" />
      </a>
    </div>
  );

  // Check if mobile device
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Error view
  if (error) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-white p-6">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-lg mb-4">{error}</p>
            <a 
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 inline-block"
            >
              Direkt öffnen
            </a>
          </div>
        </div>
      </div>
    );
  }

  // PDF View - Use react-pdf for full control over pages and zoom
  if (displayType === 'pdf') {
    return (
      <PDFViewer 
        fileUrl={fileUrl}
        fileName={fileName}
        downloadUrl={downloadUrl}
        onClose={handleClose}
        isMobile={isMobile}
      />
    );
  }

  // Image View with Zoom and Pan
  if (displayType === 'image') {
    return (
      <ImageViewer
        fileUrl={fileUrl}
        fileName={fileName}
        downloadUrl={downloadUrl}
        onClose={handleClose}
        isMobile={isMobile}
      />
    );
  }

  // DXF CAD View
  if (displayType === 'dxf') {
    return (
      <DxfViewer
        fileUrl={fileUrl}
        fileName={fileName}
        onClose={handleClose}
      />
    );
  }

  // DWG files - show message about conversion
  if (displayType === 'dwg') {
    return (
      <div className="fixed inset-0 z-[9999] bg-gray-900 flex flex-col">
        <Header bgColor="bg-gray-800" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-white p-6 max-w-md">
            <div className="w-20 h-20 bg-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl font-bold">DWG</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">AutoCAD DWG-Datei</h3>
            <p className="text-gray-400 mb-6">
              DWG-Dateien können nicht direkt angezeigt werden. 
              Bitte laden Sie die Datei herunter oder exportieren Sie sie als DXF.
            </p>
            <div className="flex flex-col gap-3">
              <a 
                href={downloadUrl} 
                download={fileName}
                className="px-6 py-3 bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Download className="w-4 h-4 inline mr-2" />
                Herunterladen
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Document View (DOCX, XLSX, PPTX without PDF preview)
  return (
    <div className="fixed inset-0 z-[9999] bg-blue-900 flex flex-col">
      <Header bgColor="bg-blue-800" />
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <FileText className="w-16 h-16 text-blue-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">
            {fileName?.toLowerCase().includes('.xlsx') || fileName?.toLowerCase().includes('.xls') ? 'Excel Tabelle' :
             fileName?.toLowerCase().includes('.pptx') || fileName?.toLowerCase().includes('.ppt') ? 'PowerPoint' :
             fileName?.toLowerCase().includes('.docx') || fileName?.toLowerCase().includes('.doc') ? 'Word Dokument' :
             'Dokument'}
          </h3>
          <p className="text-blue-200 mb-6 text-sm">{fileName}</p>
          <div className="flex flex-col gap-3">
            <a 
              href={fileUrl} 
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              📄 Öffnen
            </a>
            <a 
              href={downloadUrl} 
              download={fileName}
              className="px-6 py-3 bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Download className="w-4 h-4 inline mr-2" />
              Herunterladen
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Image Viewer with Zoom and Pan (CSS Transform based - instant response)
const ImageViewer = ({ fileUrl, fileName, downloadUrl, onClose, isMobile }) => {
  const [cssZoom, setCssZoom] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const cssZoomRef = useRef(cssZoom);
  const initialPinchDistance = useRef(0);
  const initialPinchScale = useRef(1);
  
  // Track image size for correct scroll area
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  
  // Mouse drag state for desktop
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });

  // Keep ref in sync
  useEffect(() => {
    cssZoomRef.current = cssZoom;
  }, [cssZoom]);

  // Zoom functions
  const zoomIn = () => setCssZoom(prev => Math.min(prev + 0.25, 5));
  const zoomOut = () => setCssZoom(prev => Math.max(prev - 0.25, 0.25));
  const resetZoom = () => setCssZoom(1.0);

  // Mouse handlers for desktop
  const handleMouseDown = (e) => {
    if (e.button === 0) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      if (containerRef.current) {
        setScrollStart({ x: containerRef.current.scrollLeft, y: containerRef.current.scrollTop });
      }
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollStart.x - dx;
      containerRef.current.scrollTop = scrollStart.y - dy;
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  // Double-click to zoom in - centers on clicked point
  const handleDoubleClick = (e) => {
    e.preventDefault();
    
    const container = containerRef.current;
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left + container.scrollLeft;
    const clickY = e.clientY - rect.top + container.scrollTop;
    
    const contentX = clickX / cssZoom;
    const contentY = clickY / cssZoom;
    
    const newZoom = Math.min(cssZoom + 0.5, 5);
    setCssZoom(newZoom);
    
    requestAnimationFrame(() => {
      const newClickX = contentX * newZoom;
      const newClickY = contentY * newZoom;
      
      container.scrollTo({
        left: Math.max(0, newClickX - rect.width / 2),
        top: Math.max(0, newClickY - rect.height / 2),
        behavior: 'auto'
      });
    });
  };

  // Right-click to zoom out - centers on clicked point
  const handleContextMenu = (e) => {
    e.preventDefault();
    
    const container = containerRef.current;
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left + container.scrollLeft;
    const clickY = e.clientY - rect.top + container.scrollTop;
    
    const contentX = clickX / cssZoom;
    const contentY = clickY / cssZoom;
    
    const newZoom = Math.max(cssZoom - 0.5, 0.25);
    setCssZoom(newZoom);
    
    requestAnimationFrame(() => {
      const newClickX = contentX * newZoom;
      const newClickY = contentY * newZoom;
      
      container.scrollTo({
        left: Math.max(0, newClickX - rect.width / 2),
        top: Math.max(0, newClickY - rect.height / 2),
        behavior: 'auto'
      });
    });
  };

  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setCssZoom(prev => Math.min(Math.max(prev + delta, 0.25), 5));
    }
  };

  // Handle pinch-to-zoom - centers on pinch midpoint
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let pinchCenter = { x: 0, y: 0 };
    let contentCenter = { x: 0, y: 0 };

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const getMidpoint = (touches) => {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      };
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        initialPinchDistance.current = getDistance(e.touches);
        initialPinchScale.current = cssZoomRef.current;
        
        // Calculate pinch center in container coordinates
        const rect = container.getBoundingClientRect();
        const midpoint = getMidpoint(e.touches);
        pinchCenter = {
          x: midpoint.x - rect.left + container.scrollLeft,
          y: midpoint.y - rect.top + container.scrollTop
        };
        
        // Convert to content coordinates (before zoom)
        contentCenter = {
          x: pinchCenter.x / cssZoomRef.current,
          y: pinchCenter.y / cssZoomRef.current
        };
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
        
        const currentDistance = getDistance(e.touches);
        const ratio = currentDistance / initialPinchDistance.current;
        const newZoom = Math.min(Math.max(initialPinchScale.current * ratio, 0.25), 5);
        
        // Apply the new zoom
        setCssZoom(newZoom);
        
        // Get current pinch center
        const rect = container.getBoundingClientRect();
        const currentMidpoint = getMidpoint(e.touches);
        const currentPinchX = currentMidpoint.x - rect.left;
        const currentPinchY = currentMidpoint.y - rect.top;
        
        // Calculate where the original content point is now
        const newContentX = contentCenter.x * newZoom;
        const newContentY = contentCenter.y * newZoom;
        
        // Scroll so the pinch center stays under the fingers
        requestAnimationFrame(() => {
          container.scrollLeft = newContentX - currentPinchX;
          container.scrollTop = newContentY - currentPinchY;
        });
      }
    };

    const handleTouchEnd = () => {
      initialPinchDistance.current = 0;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false, capture: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart, { capture: true });
      container.removeEventListener('touchmove', handleTouchMove, { capture: true });
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Header
  const Header = () => (
    <div className="px-3 py-2 flex items-center justify-between bg-black/90 shrink-0 safe-area-top">
      <button onClick={onClose} className="text-white hover:text-gray-300 p-1">
        <X className="w-5 h-5" />
      </button>
      <div className="text-white text-xs text-center flex-1 mx-2 truncate font-medium">
        {fileName || 'Bild'}
      </div>
      <a href={downloadUrl || fileUrl} download={fileName} className="text-white hover:text-gray-300 p-1">
        <Download className="w-5 h-5" />
      </a>
    </div>
  );

  // Controls
  const Controls = () => (
    <div className="flex items-center justify-center gap-3 py-2 bg-black/80 border-t border-gray-700">
      <button
        onClick={zoomOut}
        disabled={cssZoom <= 0.25}
        className="p-2 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white"
      >
        <ZoomOut className="w-5 h-5" />
      </button>
      <button
        onClick={resetZoom}
        className="text-white text-sm min-w-[50px] text-center bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded"
      >
        {Math.round(cssZoom * 100)}%
      </button>
      <button
        onClick={zoomIn}
        disabled={cssZoom >= 5}
        className="p-2 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white"
      >
        <ZoomIn className="w-5 h-5" />
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col" data-testid="image-preview-modal">
      <Header />
      
      <div
        ref={containerRef}
        className="flex-1 bg-gray-900"
        style={{
          overflow: 'scroll',
          touchAction: 'manipulation',
          WebkitOverflowScrolling: 'touch',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={!isMobile ? handleMouseDown : undefined}
        onMouseMove={!isMobile ? handleMouseMove : undefined}
        onMouseUp={!isMobile ? handleMouseUp : undefined}
        onMouseLeave={!isMobile ? handleMouseUp : undefined}
        onDoubleClick={!isMobile ? handleDoubleClick : undefined}
        onContextMenu={!isMobile ? handleContextMenu : undefined}
        onWheel={!isMobile ? handleWheel : undefined}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
            <div className="text-center text-white">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p>{error}</p>
            </div>
          </div>
        )}
        
        {/* Wrapper for correct scroll area calculation */}
        <div
          style={{
            minWidth: imageSize.width > 0 ? `${imageSize.width * cssZoom}px` : 'auto',
            minHeight: imageSize.height > 0 ? `${imageSize.height * cssZoom}px` : 'auto',
          }}
        >
          <div
            style={{
              transform: `scale(${cssZoom})`,
              transformOrigin: 'top left',
              transition: 'none',
              willChange: 'transform',
            }}
          >
            <img
              ref={imageRef}
              src={fileUrl}
              alt={fileName || 'Bild'}
              className="block"
              onLoad={(e) => {
                setLoading(false);
                setImageSize({ width: e.target.naturalWidth, height: e.target.naturalHeight });
              }}
              onError={() => {
                setLoading(false);
                setError('Bild konnte nicht geladen werden');
              }}
            />
          </div>
        </div>
      </div>
      
      {isMobile && !loading && (
        <div className="bg-black/80 text-center py-1.5 text-xs text-gray-300">
          👆 Wischen zum Bewegen • 👌 2 Finger zum Zoomen
        </div>
      )}
      
      {!isMobile && !loading && (
        <div className="bg-black/80 text-center py-1.5 text-xs text-gray-300">
          🖱️ Ziehen: Bewegen • Doppelklick/Rechtsklick: Zoom • Strg+Scrollrad: Stufenlos zoomen
        </div>
      )}
      
      {!loading && <Controls />}
    </div>
  );
};

// Full-featured PDF Viewer component with multi-page support and zoom
// Uses CSS transform for instant, GPU-accelerated zooming on mobile
const PDFViewer = ({ fileUrl, fileName, downloadUrl, onClose, isMobile }) => {
  const [numPages, setNumPages] = useState(null);
  const [pagesLoaded, setPagesLoaded] = useState(0);
  const [allPagesReady, setAllPagesReady] = useState(false);
  // cssZoom is applied via CSS transform (instant, GPU-accelerated)
  const [cssZoom, setCssZoom] = useState(1.0);
  // Base render scale - render once at good quality, then CSS zoom
  const baseRenderScale = isMobile ? 1.5 : 1.5; // Higher base for quality when zooming in
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  
  // Track content size for correct scroll area calculation
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 });
  
  // Mouse drag state for desktop
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
  
  // Refs for pinch-to-zoom
  const cssZoomRef = useRef(cssZoom);
  const initialPinchDistance = useRef(0);
  const initialPinchScale = useRef(1);
  
  // Update content size when pages are ready
  useEffect(() => {
    if (allPagesReady && contentRef.current) {
      setContentSize({
        width: contentRef.current.scrollWidth,
        height: contentRef.current.scrollHeight
      });
    }
  }, [allPagesReady]);

  // Convert R2 URL to proxy URL to avoid CORS issues
  const getProxyUrl = useCallback((url) => {
    if (!url) return url;
    
    // Check if it's an R2 URL (pub-*.r2.dev)
    const r2Match = url.match(/https:\/\/pub-[^/]+\.r2\.dev\/(.+)/);
    if (r2Match) {
      // Use backend proxy to avoid CORS issues
      const apiBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      return `${apiBase}/api/files/proxy/${r2Match[1]}`;
    }
    
    return url;
  }, []);

  // Get proxied URL for PDF loading
  const proxiedUrl = getProxyUrl(fileUrl);
  const [pdfUrl, setPdfUrl] = useState(proxiedUrl);

  // Update pdfUrl when fileUrl changes
  useEffect(() => {
    setPdfUrl(getProxyUrl(fileUrl));
  }, [fileUrl, getProxyUrl]);

  // Keep cssZoomRef in sync
  useEffect(() => {
    cssZoomRef.current = cssZoom;
  }, [cssZoom]);

  // Track page render completion
  const onPageLoadSuccess = useCallback(() => {
    setPagesLoaded(prev => {
      const newCount = prev + 1;
      if (numPages && newCount >= numPages) {
        setAllPagesReady(true);
      }
      return newCount;
    });
  }, [numPages]);

  // Reset page loading state when PDF changes
  useEffect(() => {
    setPagesLoaded(0);
    setAllPagesReady(false);
  }, [pdfUrl]);

  // Retry logic
  const handleRetry = useCallback(() => {
    if (retryCount < 3) {
      setLoading(true);
      setError(null);
      setPagesLoaded(0);
      setAllPagesReady(false);
      const baseUrl = getProxyUrl(fileUrl);
      const newUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}retry=${Date.now()}`;
      setPdfUrl(newUrl);
      setRetryCount(prev => prev + 1);
    }
  }, [fileUrl, retryCount, getProxyUrl]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setRetryCount(0);
    console.log(`✅ PDF loaded: ${numPages} pages`);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error('❌ PDF load error:', error);
    if (retryCount < 3) {
      setTimeout(() => {
        handleRetry();
      }, 1000 * (retryCount + 1));
    } else {
      setError('PDF konnte nicht geladen werden');
      setLoading(false);
    }
  }, [retryCount, handleRetry]);

  // CSS-based zoom functions - instant, no re-render
  const zoomIn = () => setCssZoom(prev => Math.min(prev + 0.25, 4));
  const zoomOut = () => setCssZoom(prev => Math.max(prev - 0.25, 0.3));
  const resetZoom = () => setCssZoom(1.0);

  // Handle pinch-to-zoom on touch devices - uses CSS transform for instant response
  // Now centers zoom on the midpoint between fingers
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let pinchCenter = { x: 0, y: 0 };
    let contentCenter = { x: 0, y: 0 };

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const getMidpoint = (touches) => {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      };
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        initialPinchDistance.current = getDistance(e.touches);
        initialPinchScale.current = cssZoomRef.current;
        
        // Calculate pinch center in container coordinates
        const rect = container.getBoundingClientRect();
        const midpoint = getMidpoint(e.touches);
        pinchCenter = {
          x: midpoint.x - rect.left + container.scrollLeft,
          y: midpoint.y - rect.top + container.scrollTop
        };
        
        // Convert to content coordinates (before zoom)
        contentCenter = {
          x: pinchCenter.x / cssZoomRef.current,
          y: pinchCenter.y / cssZoomRef.current
        };
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
        
        const currentDistance = getDistance(e.touches);
        const ratio = currentDistance / initialPinchDistance.current;
        const newZoom = Math.min(Math.max(initialPinchScale.current * ratio, 0.3), 4);
        
        // Apply the new zoom
        setCssZoom(newZoom);
        
        // Get current pinch center
        const rect = container.getBoundingClientRect();
        const currentMidpoint = getMidpoint(e.touches);
        const currentPinchX = currentMidpoint.x - rect.left;
        const currentPinchY = currentMidpoint.y - rect.top;
        
        // Calculate where the original content point is now
        const newContentX = contentCenter.x * newZoom;
        const newContentY = contentCenter.y * newZoom;
        
        // Scroll so the pinch center stays under the fingers
        requestAnimationFrame(() => {
          container.scrollLeft = newContentX - currentPinchX;
          container.scrollTop = newContentY - currentPinchY;
        });
      }
    };

    const handleTouchEnd = () => {
      initialPinchDistance.current = 0;
    };

    // Use capture phase and prevent default to intercept before browser handles it
    container.addEventListener('touchstart', handleTouchStart, { passive: false, capture: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart, { capture: true });
      container.removeEventListener('touchmove', handleTouchMove, { capture: true });
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, []); // Empty deps - uses refs to avoid stale closures

  // Mouse handlers for desktop drag-to-pan
  const handleMouseDown = (e) => {
    if (e.button === 0) { // Left click
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      if (containerRef.current) {
        setScrollStart({ 
          x: containerRef.current.scrollLeft, 
          y: containerRef.current.scrollTop 
        });
      }
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollStart.x - dx;
      containerRef.current.scrollTop = scrollStart.y - dy;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Double-click to zoom in - centers on clicked point
  const handleDoubleClick = (e) => {
    e.preventDefault();
    
    const container = containerRef.current;
    if (!container) return;
    
    // Get click position relative to container
    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left + container.scrollLeft;
    const clickY = e.clientY - rect.top + container.scrollTop;
    
    // Calculate position in content coordinates (before zoom)
    const contentX = clickX / cssZoom;
    const contentY = clickY / cssZoom;
    
    // New zoom level
    const newZoom = Math.min(cssZoom + 0.5, 4);
    setCssZoom(newZoom);
    
    // After zoom, scroll so the clicked point is centered
    requestAnimationFrame(() => {
      // New position of clicked point after zoom
      const newClickX = contentX * newZoom;
      const newClickY = contentY * newZoom;
      
      // Center in viewport
      const centerX = newClickX - rect.width / 2;
      const centerY = newClickY - rect.height / 2;
      
      container.scrollTo({
        left: Math.max(0, centerX),
        top: Math.max(0, centerY),
        behavior: 'auto' // instant, not smooth
      });
    });
  };

  // Right-click to zoom out - centers on clicked point
  const handleContextMenu = (e) => {
    e.preventDefault();
    
    const container = containerRef.current;
    if (!container) return;
    
    // Get click position relative to container
    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left + container.scrollLeft;
    const clickY = e.clientY - rect.top + container.scrollTop;
    
    // Calculate position in content coordinates (before zoom)
    const contentX = clickX / cssZoom;
    const contentY = clickY / cssZoom;
    
    // New zoom level
    const newZoom = Math.max(cssZoom - 0.5, 0.3);
    setCssZoom(newZoom);
    
    // After zoom, scroll so the clicked point stays centered
    requestAnimationFrame(() => {
      const newClickX = contentX * newZoom;
      const newClickY = contentY * newZoom;
      
      const centerX = newClickX - rect.width / 2;
      const centerY = newClickY - rect.height / 2;
      
      container.scrollTo({
        left: Math.max(0, centerX),
        top: Math.max(0, centerY),
        behavior: 'auto'
      });
    });
  };

  // Mouse wheel zoom for desktop
  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setCssZoom(prev => Math.min(Math.max(prev + delta, 0.3), 4));
    }
  };

  // Header component
  const Header = () => (
    <div className="px-3 py-2 flex items-center justify-between bg-gray-800 shrink-0 safe-area-top">
      <button
        onClick={onClose}
        className="flex items-center gap-1 text-white hover:text-gray-300 transition-colors p-1"
        aria-label="Schließen"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="text-white text-xs text-center flex-1 mx-2 truncate font-medium">
        {fileName || 'PDF'}
      </div>
      <a 
        href={downloadUrl || fileUrl} 
        download={fileName}
        className="text-white hover:text-gray-300 p-1 transition-colors"
        title="Herunterladen"
        aria-label="Herunterladen"
      >
        <Download className="w-5 h-5" />
      </a>
    </div>
  );

  // Controls bar - uses cssZoom for instant response
  const Controls = () => (
    <div className="flex items-center justify-center gap-3 py-2 bg-gray-700 border-t border-gray-600">
      {/* Page info */}
      <span className="text-white text-sm">
        {numPages} {numPages === 1 ? 'Seite' : 'Seiten'}
      </span>

      <div className="w-px h-6 bg-gray-500" />

      {/* Zoom controls - CSS-based, instant */}
      <button
        onClick={zoomOut}
        disabled={cssZoom <= 0.3}
        className="p-2 rounded bg-gray-600 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed text-white"
        aria-label="Verkleinern"
      >
        <ZoomOut className="w-5 h-5" />
      </button>
      
      <button
        onClick={resetZoom}
        className="text-white text-sm min-w-[50px] text-center bg-gray-600 hover:bg-gray-500 px-2 py-1 rounded"
        title="Zoom zurücksetzen"
      >
        {Math.round(cssZoom * 100)}%
      </button>
      
      <button
        onClick={zoomIn}
        disabled={cssZoom >= 4}
        className="p-2 rounded bg-gray-600 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed text-white"
        aria-label="Vergrößern"
      >
        <ZoomIn className="w-5 h-5" />
      </button>
    </div>
  );

  if (error) {
    // Fallback to direct link on error after all retries
    return (
      <div className="fixed inset-0 z-[9999] bg-gray-900 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-white p-6">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-lg mb-2">PDF konnte nicht geladen werden</p>
            <p className="text-gray-400 text-sm mb-4">
              {retryCount >= 3 ? 'Alle Versuche fehlgeschlagen' : ''}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setRetryCount(0);
                  setError(null);
                  setLoading(true);
                  setPdfUrl(`${fileUrl}?force=${Date.now()}`);
                }}
                className="px-6 py-2 bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Erneut versuchen
              </button>
              <a 
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 inline-block"
              >
                Direkt öffnen
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-gray-900 flex flex-col" data-testid="pdf-viewer-modal">
      <Header />
      
      <div 
        ref={containerRef}
        className="flex-1 bg-gray-800"
        style={{ 
          overflow: 'scroll', // Enable scrolling in both directions
          touchAction: 'manipulation', // Allow pan in all directions + pinch-zoom
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={!isMobile ? handleMouseDown : undefined}
        onMouseMove={!isMobile ? handleMouseMove : undefined}
        onMouseUp={!isMobile ? handleMouseUp : undefined}
        onMouseLeave={!isMobile ? handleMouseUp : undefined}
        onDoubleClick={!isMobile ? handleDoubleClick : undefined}
        onContextMenu={!isMobile ? handleContextMenu : undefined}
        onWheel={!isMobile ? handleWheel : undefined}
      >
        {/* Loading overlay - shows until document is loaded OR all pages are rendered */}
        {(loading || (numPages && !allPagesReady)) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
            <div className="text-white text-center">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              {loading ? (
                <>
                  <p className="text-lg">PDF wird geladen...</p>
                  {retryCount > 0 && (
                    <p className="text-gray-400 text-sm mt-1">Versuch {retryCount}/3</p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-lg">Seiten werden vorbereitet...</p>
                  <p className="text-gray-400 text-sm mt-2">
                    {pagesLoaded} von {numPages} Seiten
                  </p>
                  {/* Progress bar */}
                  <div className="w-48 h-2 bg-gray-700 rounded-full mt-3 mx-auto overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${(pagesLoaded / numPages) * 100}%` }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* PDF Content with CSS Transform for instant zoom */}
        {/* Wrapper div that provides correct scroll area based on zoom */}
        <div
          style={{
            // This wrapper needs to be the actual size of the zoomed content
            // so scrolling works correctly at all zoom levels
            minWidth: contentSize.width > 0 ? `${contentSize.width * cssZoom}px` : 'auto',
            minHeight: contentSize.height > 0 ? `${contentSize.height * cssZoom}px` : 'auto',
          }}
        >
          <div
            ref={contentRef}
            style={{
              transform: `scale(${cssZoom})`,
              transformOrigin: 'top left', // Scale from top-left so scrolling works in all directions
              transition: 'none', // No transition for instant response
              willChange: 'transform', // GPU acceleration hint
            }}
          >
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
              className="py-4 flex flex-col items-start gap-4"
            >
              {/* Render ALL pages once at base scale, then CSS zoom */}
              {numPages && Array.from({ length: numPages }, (_, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  scale={baseRenderScale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-lg bg-white"
                  onRenderSuccess={onPageLoadSuccess}
                  loading={
                    <div className="w-64 h-96 bg-gray-700 animate-pulse flex items-center justify-center rounded">
                      <span className="text-gray-400">Seite {index + 1}...</span>
                    </div>
                  }
                />
              ))}
            </Document>
          </div>
        </div>
      </div>
      
      {/* Hint for mobile - only show when fully loaded */}
      {isMobile && allPagesReady && (
        <div className="bg-gray-700 text-center py-1.5 text-xs text-gray-300 safe-area-bottom">
          👆 Wischen zum Bewegen • 👌 2 Finger zum Zoomen
        </div>
      )}
      
      {/* Hint for desktop - only show when fully loaded */}
      {!isMobile && allPagesReady && (
        <div className="bg-gray-700 text-center py-1.5 text-xs text-gray-300">
          🖱️ Ziehen: Bewegen • Doppelklick/Rechtsklick: Zoom • Strg+Scrollrad: Stufenlos zoomen
        </div>
      )}
      
      {/* Controls - only show when pages are ready */}
      {allPagesReady && <Controls />}
    </div>
  );
};

export default FilePreview;
